"use client";

import {
  createContext,
  useContext,
  useMemo,
  useState,
  PropsWithChildren,
} from "react";
import {
  DATE_ASC,
  DATE_DESC,
  TOTAL_ASC,
  TOTAL_DESC,
  SPREAD_ASC,
  SPREAD_DESC,
  MARGIN_ASC,
  MARGIN_DESC,
} from "@/constants/schedule";
import { GetNflScheduleQuery, NflScheduleGame } from "@/graphql/gql/graphql";
import * as sortUtils from "../sortGames";
import { filterEmpty } from "..";
import useGetNflSchedules from "./useGetNflSchedules";

export type TabsOptions = "teams" | "players";

interface ScheduledGamesContextType {
  games: ScheduledGamesT;
  onTeamFilterChange: (conf: string) => void;
  onTeamSortChange: (sort: string) => void;
  sortBy: string;
  isError: boolean;
}
export type ScheduledGamesT = GetNflScheduleQuery["getNflSchedule"];
export type ScheduledGameT = NonNullable<
  NonNullable<GetNflScheduleQuery["getNflSchedule"]>[number]
>;

export const ScheduledGamesContext =
  createContext<ScheduledGamesContextType | null>(null);

export function ScheduledGamesContextProvider({
  children,
  initialGames,
  week,
}: PropsWithChildren<{
  initialGames: ScheduledGamesT;
  week: number | undefined;
}>) {
  const [teamFilter, setTeamFilter] = useState<string>("all");
  const [teamSort, setTeamSort] = useState<string>(DATE_ASC);

  const formattedInitialData: GetNflScheduleQuery = {
    getNflSchedule: initialGames,
  };

  const { liveSchedule, isError } = useGetNflSchedules(
    formattedInitialData,
    week
  );

  const filteredGames = useMemo(() => {
    if (teamFilter === "all") return liveSchedule;
    return liveSchedule?.filter(
      (game) =>
        game?.homeTeam?.conference === teamFilter ||
        game?.awayTeam?.conference === teamFilter
    );
  }, [teamFilter, liveSchedule]);

  const sortedGames: ScheduledGamesT | null = useMemo(() => {
    if (!filteredGames) {
      return null;
    }
    if (teamSort === DATE_ASC) {
      return filteredGames;
    }
    const sortableGames = filterEmpty(
      filteredGames.slice()
    ) as NflScheduleGame[];

    if (teamSort === DATE_DESC) {
      return sortableGames.reverse();
    }

    if (teamSort === TOTAL_DESC) {
      return sortUtils.gamesByTotalDesc(sortableGames);
    }
    if (teamSort === TOTAL_ASC) {
      return sortUtils.gamesByTotalAsc(sortableGames);
    }
    if (teamSort === SPREAD_ASC) {
      return sortUtils.gamesBySpreadAsc(sortableGames);
    }
    if (teamSort === SPREAD_DESC) {
      return sortUtils.gamesBySpreadDesc(sortableGames);
    }

    if (teamSort === MARGIN_ASC) {
      return sortUtils.gamesByMarginAsc(sortableGames);
    }

    if (teamSort === MARGIN_DESC) {
      return sortUtils.gamesByMarginDesc(sortableGames);
    }
    return filteredGames;
  }, [filteredGames, teamSort]);

  const gamesInfo: ScheduledGamesContextType = useMemo(
    () => ({
      onTeamFilterChange: setTeamFilter,
      onTeamSortChange: setTeamSort,
      games: sortedGames as ScheduledGamesT,
      sortBy: teamSort,
      isError: isError,
    }),
    [sortedGames, teamSort, isError]
  );

  return (
    <ScheduledGamesContext.Provider value={gamesInfo}>
      {children}
    </ScheduledGamesContext.Provider>
  );
}

export const useScheduledGamesFilter = () => {
  const scheduledGamesInfo = useContext(ScheduledGamesContext);
  return scheduledGamesInfo;
};
